import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { individualResultsApi } from '../../api/individualResultsApi';
import { formatTime } from '../../utils/timeFormat';
import type { Discipline } from '../../types/domain';

interface Props {
  discipline: Discipline;
}

export function TeamSumRankingTable({ discipline }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['team-sum-ranking', discipline.id],
    queryFn: () => individualResultsApi.getTeamSumRanking(discipline.id),
    enabled: !!discipline.teamScoringCount,
    refetchInterval: 5_000,
  });

  if (!discipline.teamScoringCount) return null;
  if (isLoading) return <Typography>Načítám…</Typography>;
  const items = data ?? [];
  const fmt = discipline.timeFormat ?? 'SS_CC';

  return (
    <Paper variant="outlined">
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2">
          Družstvo = součet {discipline.teamScoringCount} nejlepších časů
          {discipline.teamMaxRunners ? ` z ${discipline.teamMaxRunners} závodníků` : ''}
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Pořadí</TableCell>
            <TableCell>Družstvo</TableCell>
            <TableCell>Závodníci ({discipline.teamScoringCount} nejlepších)</TableCell>
            <TableCell align="right">Součet</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.teamId} hover>
              <TableCell>
                {it.rank != null ? (
                  `${it.rank}.`
                ) : (
                  <Chip
                    size="small"
                    label={`Nehodnoceno (${it.validAthletes}/${it.minRequired})`}
                    color="warning"
                  />
                )}
              </TableCell>
              <TableCell>{it.teamName}</TableCell>
              <TableCell>
                {it.contributors.map((a, idx) => (
                  <Box
                    key={a.athleteId}
                    component="span"
                    sx={{
                      display: 'inline-block',
                      mr: 2,
                      fontFamily: 'monospace',
                      fontSize: 13,
                    }}
                  >
                    {a.lastName} {a.firstName.charAt(0)}. — {formatTime(a.bestTimeMs, fmt)}
                    {idx < it.contributors.length - 1 && ','}
                  </Box>
                ))}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                {it.totalTimeMs != null ? formatTime(it.totalTimeMs, fmt) : '—'}
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                Žádná družstva
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}
